import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'universal-cookie';
import { jwtDecode } from 'jwt-decode';
import dayjs from 'dayjs';

import { getCookieNameSuffix as getWebCookieNameSuffix } from 'utils/cookies';
import { MissingRefreshTokenError, TokenStorageUnavailableError } from 'services/auth/authErrors';
import { isNativePlatform } from 'services/auth/platform';
import * as tokenStore from 'services/auth/tokenStore';

/**
 * Single module for how auth tokens are stored and attached to requests.
 *
 * - Web: the JWT lives in cookies (`AF_ATHP` header.payload readable by JS, `AF_ATS` signature + `AF_RT` refresh httpOnly)
 *   and is sent automatically via `withCredentials`.
 * - Native (Capacitor): the JWT is held in an in-memory cache backed by the iOS Keychain / Android Keystore
 *   and sent as an `Authorization: Bearer` header.
 *
 * Everything that reads the token or applies credentials goes through this module so the native swap is isolated here.
 */

export interface SessionClaims {
  client?: string;
  authAt?: number;
  iat?: number;
}

export interface AuthTransport {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  getSessionClaims(): SessionClaims | null;
  isAccessTokenExpiringSoon(thresholdMinutes: number): boolean;
  isStorageUnavailable(): boolean;
  retryHydrate(): Promise<void>;
  applyAuthToConfig(config: AxiosRequestConfig): AxiosRequestConfig;
  applyRefreshAuthToConfig(config: AxiosRequestConfig): AxiosRequestConfig;
  persistTokensFromResponse(response: AxiosResponse): Promise<void>;
  clearStoredTokens(): Promise<void>;
}

interface AuthTokensPayload {
  accessToken?: string;
  refreshToken?: string;
}

const readSessionClaims = (accessToken: string | null): SessionClaims | null => {
  if (!accessToken) return null;

  try {
    const { client, authAt, iat } = jwtDecode<SessionClaims>(accessToken);
    return { client, authAt, iat };
  } catch {
    return null;
  }
};

const isExpiringSoon = (accessToken: string | null, thresholdMinutes: number): boolean => {
  if (!accessToken) return false;

  try {
    const decoded = jwtDecode(accessToken);
    if (!decoded?.exp) return false;

    return dayjs(decoded.exp * 1000).diff(dayjs(), 'minute') <= thresholdMinutes;
  } catch {
    return false;
  }
};

const withCredentials = (config: AxiosRequestConfig): AxiosRequestConfig => ({ ...config, withCredentials: true });

const webTransport: AuthTransport = {
  getAccessToken: () => {
    if (typeof window === 'undefined') return null;

    const cookies = new Cookies(null, { path: '/' });
    return cookies.get(`AF_ATHP${getWebCookieNameSuffix()}`) || null;
  },
  // The browser sends the httpOnly refresh cookie automatically; JS never reads it.
  getRefreshToken: () => null,
  getSessionClaims: () => readSessionClaims(webTransport.getAccessToken()),
  isAccessTokenExpiringSoon: (thresholdMinutes) => isExpiringSoon(webTransport.getAccessToken(), thresholdMinutes),
  isStorageUnavailable: () => false,
  retryHydrate: () => Promise.resolve(),
  applyAuthToConfig: withCredentials,
  applyRefreshAuthToConfig: withCredentials,
  // The browser persists the cookies set by the response; nothing to store here.
  persistTokensFromResponse: () => Promise.resolve(),
  clearStoredTokens: () => Promise.resolve()
};

const nativeTransport: AuthTransport = {
  getAccessToken: () => tokenStore.getAccessToken(),
  getRefreshToken: () => tokenStore.getRefreshToken(),
  getSessionClaims: () => readSessionClaims(tokenStore.getAccessToken()),
  isAccessTokenExpiringSoon: (thresholdMinutes) => isExpiringSoon(tokenStore.getAccessToken(), thresholdMinutes),
  isStorageUnavailable: () => tokenStore.isStorageUnavailable(),
  retryHydrate: () => tokenStore.retryHydrate(),
  applyAuthToConfig: (config) => {
    const accessToken = tokenStore.getAccessToken();
    if (!accessToken) return config;

    return {
      ...config,
      headers: { ...config.headers, Authorization: `Bearer ${accessToken}` }
    };
  },
  applyRefreshAuthToConfig: (config) => {
    if (tokenStore.isStorageUnavailable()) {
      throw new TokenStorageUnavailableError();
    }

    const refreshToken = tokenStore.getRefreshToken();
    if (!refreshToken) {
      throw new MissingRefreshTokenError();
    }

    return {
      ...config,
      headers: { ...config.headers, Authorization: `Bearer ${refreshToken}` }
    };
  },
  persistTokensFromResponse: async (response) => {
    const tokens = (response?.data as { tokens?: AuthTokensPayload } | undefined)?.tokens;
    if (!tokens) return;

    await tokenStore.setTokens({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
  },
  clearStoredTokens: () => tokenStore.clear()
};

const transport: AuthTransport = isNativePlatform() ? nativeTransport : webTransport;

export const getAccessToken = (): string | null => transport.getAccessToken();
export const getRefreshToken = (): string | null => transport.getRefreshToken();
export const getSessionClaims = (): SessionClaims | null => transport.getSessionClaims();
export const isAccessTokenExpiringSoon = (thresholdMinutes: number): boolean => transport.isAccessTokenExpiringSoon(thresholdMinutes);
export const isStorageUnavailable = (): boolean => transport.isStorageUnavailable();
export const retryHydrate = (): Promise<void> => transport.retryHydrate();
export const applyAuthToConfig = (config: AxiosRequestConfig): AxiosRequestConfig => transport.applyAuthToConfig(config);
export const applyRefreshAuthToConfig = (config: AxiosRequestConfig): AxiosRequestConfig => transport.applyRefreshAuthToConfig(config);
export const persistTokensFromResponse = (response: AxiosResponse): Promise<void> => transport.persistTokensFromResponse(response);
export const clearStoredTokens = (): Promise<void> => transport.clearStoredTokens();
